import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, TextField, Button, Chip, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Alert, ToggleButtonGroup, ToggleButton,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import api from "../services/api";

// Returns today as YYYY-MM-DD using local date (not UTC).
function todayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const CATEGORY_PRESETS = [
  { label: "All",       value: null },
  { label: "Copper",    value: "COPPER" },
  { label: "Aluminum",  value: "ALUMINUM" },
  { label: "Brass",     value: "BRASS" },
  { label: "Lead",      value: "LEAD" },
  { label: "Stainless", value: "STAINLESS" },
  { label: "Wire",      value: "WIRE" },
  { label: "Motors",    value: "MOTORS" },
];

export default function PriceDashboard() {
  const [date, setDate]               = useState(todayStr());
  const [category, setCategory]       = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [vendors, setVendors]         = useState([]);
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [stats, setStats]             = useState({ total: 0, matched: 0 });
  const [latestDate, setLatestDate]   = useState(null);   // most recent date with rows

  // Fetch vendors + most-recent-date-with-data once
  useEffect(() => {
    let alive = true;
    api.get("/prices/vendors").then((r) => {
      if (alive) setVendors(r.data?.vendors || []);
    }).catch(() => {});

    api.get("/prices/latestDate").then((r) => {
      const latest = r.data?.latest;
      if (!alive || !latest) return;
      setLatestDate(latest);
      // If today has no data yet (weekend / pre-9 AM / vendor late),
      // default the view to whatever date actually has prices loaded.
      if (latest < todayStr()) setDate(latest);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Fetch comparison whenever date/category changes (and search is empty)
  useEffect(() => {
    if (searchQuery.trim()) return; // search effect handles this case
    let alive = true;
    setLoading(true);
    setError("");
    const params = { date };
    if (category) params.category = category;

    api.get("/prices/comparison", { params })
      .then((r) => {
        if (!alive) return;
        const rs = r.data?.rows || [];
        setRows(rs);
        setStats({ total: rs.length, matched: rs.length });
      })
      .catch((err) => {
        if (!alive) return;
        setError(err.response?.data?.message || "Failed to load prices");
        setRows([]);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [date, category, searchQuery]);

  // Search effect
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) return;
    const t = setTimeout(() => {
      let alive = true;
      setLoading(true);
      setError("");
      api.get("/prices/search", { params: { q, date } })
        .then((r) => {
          if (!alive) return;
          setRows(r.data?.rows || []);
          setStats({ total: r.data?.count || 0, matched: r.data?.count || 0 });
        })
        .catch((err) => {
          if (!alive) return;
          setError(err.response?.data?.message || "Search failed");
          setRows([]);
        })
        .finally(() => alive && setLoading(false));
      return () => { alive = false; };
    }, 250); // debounce
    return () => clearTimeout(t);
  }, [searchQuery, date]);

  const vendorCodes = useMemo(
    () => vendors.filter((v) => v.IsActive).map((v) => v.Code),
    [vendors],
  );

  const fmtPrice = (p) => (p == null ? "—" : `$${Number(p).toFixed(2)}`);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h6" sx={{ mb: 2, color: "black" }}>
        Daily Price Comparison
      </Typography>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", md: 180 } }}
          />

          <TextField
            placeholder="Search code (CU, AL, BRASS) or item..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            size="small"
            onClick={() => { setSearchQuery(""); setCategory(null); setDate(todayStr()); }}
          >
            Reset
          </Button>
        </Stack>

        <ToggleButtonGroup
          value={category}
          exclusive
          onChange={(_, v) => { setCategory(v); setSearchQuery(""); }}
          size="small"
          sx={{ mt: 2, flexWrap: "wrap" }}
        >
          {CATEGORY_PRESETS.map((c) => (
            <ToggleButton key={c.label} value={c.value}>{c.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      {/* Status row */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        {loading && <CircularProgress size={16} />}
        <Typography variant="body2" color="text.secondary">
          {stats.total} {stats.total === 1 ? "material" : "materials"}
          {searchQuery && ` matching "${searchQuery}"`}
          {category && !searchQuery && ` in ${category}`}
          {" · "}
          {date}
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stale-date banner: today has no prices yet, viewing latest available */}
      {latestDate && date === latestDate && latestDate < todayStr() && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No price sheets received yet for {todayStr()}. Showing the most recent
          available date, <b>{latestDate}</b>. The script re-checks Outlook every
          hour from 7 AM to 4 PM and the dashboard will switch to today automatically
          once any vendor sends their email.
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", minWidth: 240 }}>Material</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
              {vendorCodes.map((code) => (
                <TableCell
                  key={code}
                  align="right"
                  sx={{ fontWeight: "bold", minWidth: 90 }}
                >
                  {code}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Best</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Spread</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={vendorCodes.length + 4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No prices for {date}
                  {category ? ` in ${category}` : ""}
                  {searchQuery ? ` matching "${searchQuery}"` : ""}.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.canonical} hover>
                <TableCell>{r.canonical}</TableCell>
                <TableCell>
                  <Chip label={r.category || "—"} size="small" variant="outlined" />
                </TableCell>
                {vendorCodes.map((code) => {
                  const price = r.prices?.[code];
                  const isBest = price != null && code === r.best && Object.keys(r.prices).length > 1;
                  return (
                    <TableCell
                      key={code}
                      align="right"
                      sx={{
                        fontWeight: isBest ? "bold" : "normal",
                        color: isBest ? "success.main" : (price == null ? "text.disabled" : "inherit"),
                        fontFamily: "monospace",
                      }}
                      title={r.rawItems?.[code]}
                    >
                      {fmtPrice(price)}
                    </TableCell>
                  );
                })}
                <TableCell align="right">
                  {r.best ? <Chip label={r.best} size="small" color="success" /> : "—"}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                  {r.spread != null ? `$${Number(r.spread).toFixed(2)}` : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
